import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { Destination, History, LineDelay, LinkDiag, SourceInfo, StreamSummary } from './types';
import { EyeIcon, viewersText } from './PlatformBlock';
import { backlogSec, delaySec, serverName } from './format';
import { dips, historyStat, wavePaths, type StateColor } from './quality';
import { useCounter } from './useCounter';
import { ObsSettings } from './ObsSettings';
import { SrtLinkPanel } from './SrtLinkPanel';
import { StreamPreview } from './StreamPreview';
import { BG12, BORDER, BORDER50, EditButton, FILL, LogoSlot, RelayToggle, STROKE, TXT } from './ui';
import { Icon, SvgIcon, type IconName } from '../ui/Icon';
import { useBadgePulse } from '../ui/motion';
import { driftText, driftWarn, type PairView } from './vertical';
import { verticalVideo } from './studio/Pair';
import { t } from '../i18n';

/*
 * Desktop signal map: SOURCE (your PC / OBS) → the ingest node → one route per destination
 * (the system's SignalMap: nodes on bg with a state edge, 2 px routes at 0° / 45° with chamfered
 * bends; AUDIT ST-02). Variant "2B": each route has a summary label near its node; hover/focus opens
 * a 5-minute history popover, click pins it. Coordinates are in a 1040-wide viewBox; HTML overlays
 * are positioned in % of it. The source node grows by a 16:9 stream preview while the stream is
 * live (docs/m15-live-stats.md 1.1).
 *
 * Motion (07 «one heartbeat per view», AUDIT ST-03, MO-02, MO-09): one travelling dash on the live
 * uplink, ≤ 1 dash/s, and only where no animated logo is in view; nothing else on the map loops.
 * A problem is a dashed route, a failure a cut route with a cross — no flicker.
 */
const VB_W = 1040;
/** source node: x, width; preview inside it */
const SRC_X = 12;
const SRC_W = 224;
const PV_X = 24;
const PV_W = SRC_W - 2 * (PV_X - SRC_X);
const PV_H = (PV_W * 9) / 16;
/** extra height of the source node with the preview */
const PV_GROW = Math.ceil(PV_H) + 12;
/** the ingest node */
const N_X = 330;
const N_W = 150;
/** where the routes fan out; up to TRUNK_MAX off the middle a route is one 45° run */
const TRUNK = 500;
const TRUNK_MAX = 300;
/** destination nodes */
const D_X = 820;
const D_W = 200;

type LinkInfo = { id: string; label: string; pill: string; color: StateColor; history: History; event: string; x: number; y: number; dest?: Destination };

function pillFor(d: Destination): { text: string; color: StateColor } {
  if (d.state === 'failed') return { text: 'FAILED', color: 'fail' };
  if (d.state === 'unknown') return { text: 'NO DATA', color: 'idle' };
  // no data yet is not a starting state: neutral (AUDIT ST-04)
  if (d.state === 'starting' && !d.history.some((p) => p.v > 0)) return { text: 'NO DATA YET', color: 'idle' };
  if (d.state === 'waiting') return d.needsKey ? { text: 'NEEDS KEY', color: 'warn' } : { text: 'WAITING', color: 'idle' };
  if (d.state === 'stopped' || d.state === 'stopping') return { text: 'OFF', color: 'idle' };
  if (d.state === 'reconnecting' || d.state === 'starting' || d.issue) return { text: 'UNSTABLE', color: 'warn' };
  if (!d.history.some((p) => p.v > 0)) return { text: 'NO DATA YET', color: 'idle' };
  const n = dips(d.history).count;
  return n ? { text: `${n} DIP${n > 1 ? 'S' : ''}`, color: 'warn' } : { text: '5 MIN OK', color: 'live' };
}

function nodeLine(d: Destination, now: number): { text: string; color: StateColor } {
  switch (d.state) {
    case 'live': return d.issue ? { text: d.issue.replace('_', ' ').toUpperCase(), color: 'warn' } : { text: `LIVE · ${d.bitrateMbps?.toFixed(1) ?? '—'} Mbps`, color: 'live' };
    case 'starting': return { text: 'CONNECTING…', color: 'start' };
    case 'reconnecting': {
      const left = d.retryAt ? Math.max(0, Math.ceil((d.retryAt - now) / 1000)) : 0;
      return { text: left ? `RETRY IN ${left} S` : 'RETRYING', color: 'warn' };
    }
    case 'failed': return { text: 'FAILED', color: 'fail' };
    case 'stopping': return { text: 'STOPPING', color: 'idle' };
    case 'stopped': return { text: 'OFF', color: 'idle' };
    case 'unknown': return { text: 'NO DATA', color: 'idle' };
    default: return d.needsKey ? { text: 'NEEDS KEY', color: 'warn' } : { text: 'WAITING', color: 'idle' };
  }
}

/** A system icon inside the map's SVG, at the given point; a starting state turns (as its chip does). */
function MapIcon({ name, x, y, size = 12, className, turn }: { name: IconName; x: number; y: number; size?: 12 | 16; className?: string; turn?: boolean }) {
  return <g className={className}><SvgIcon name={name} x={x} y={y} size={size} className={turn ? 'sx-turn' : undefined} /></g>;
}

/** Route of a destination: 0° and 45° only, the bend chamfered (05 «45° geometry», AUDIT ST-02). */
function routeFor(cy: number, y: number, end: number): string {
  const dy = y - cy;
  const k = Math.abs(dy);
  if (k <= TRUNK_MAX) return `M${N_X + N_W} ${cy} H${TRUNK} L${TRUNK + k} ${y} H${end}`;
  // far lines: 45° out, down the trunk, 45° in
  const s = Math.sign(dy);
  return `M${N_X + N_W} ${cy} H${TRUNK} L${TRUNK + 40} ${cy + s * 40} V${y - s * 40} L${TRUNK + 80} ${y} H${end}`;
}

/** VERT-1 (VT-03): the vertical input's port is the node's lower one, this far under the middle. */
const V_PORT = 24;
/** Height of the vertical source node, and its gap under the main one. */
const V_H = 74;
const V_GAP = 22;

/** Does this destination's line start at the vertical port (it sends the vertical input now)? */
const fromVertical = (d: Destination) => !!d.vertical?.tag && !d.vertical.state && d.vertical.label !== 'main';

export function SignalMap({ source, dests, now, obsOpen, onObs, onObsClose, viewers, editing, onEdit, onToggle, pair }: {
  source: SourceInfo; dests: Destination[]; now: number; obsOpen: boolean; onObs: () => void; onObsClose: () => void;
  /** VERT-1 (mockup E, VT-03, VT-16): the vertical input — a second source node, the node's lower port, 9:16 on its lines */
  pair?: PairView;
  /** Watching from the summary: "N watching on M platforms" */
  viewers?: StreamSummary['viewers'];
  /** the output whose settings are open, and the edit button on each destination node (Status) */
  editing?: number; onEdit?: (id: number) => void;
  /** turns an output on or off from its line's chip (Status, owner 19.10) */
  onToggle?: (d: Destination) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  // With the preview the node's header moves up by half the growth and its body down by the other half.
  const pv = source.preview;
  const lift = pv ? PV_GROW / 2 : 0;
  const down = pv ? PV_GROW / 2 : 0;
  const pvY = (cy: number) => cy - 14 - lift;
  // "Delay to us" adds two rows under the upload line; the node grows downwards.
  const dl = source.delay;
  const extra = dl ? 32 : 0;

  // Escape closes an open or pinned panel; focus goes back to its label. Moving
  // the focus there must not reopen the panel (onFocus opens it).
  const restoring = useRef(false);
  useEffect(() => {
    if (!open && !pinned) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const id = pinned ?? open;
      setPinned(null);
      setOpen(null);
      const el = document.querySelector<HTMLElement>(`[data-panel-for="${id}"]`);
      if (el && document.activeElement !== el) {
        restoring.current = true;
        el.focus({ preventScroll: true });
        restoring.current = false;
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [open, pinned]);

  const n = dests.length;
  const compact = n > 6;
  const step = compact ? 40 : 80;
  const vp = pair?.on ? pair : undefined;
  // the vertical source sits under the main one: the map grows by it
  const H0 = Math.max(pv ? 360 : 300, n * step + 60);
  // the main source's bottom under the middle (preview, delay rows), then the vertical node: cy = H / 2 must leave room for both
  const below = 70 + (pv ? PV_GROW / 2 : 0) + (source.delay ? 32 : 0)
  const H = vp ? Math.max(H0, 2 * (below + V_GAP + V_H + 12)) : H0;
  const cy = H / 2;
  const vTop = cy + 70 + (pv ? PV_GROW / 2 : 0) + (source.delay ? 32 : 0) + V_GAP;
  const vy = vTop + V_H / 2;
  const vWarn = driftWarn(vp?.driftMs);
  const vState: StateColor = !vp ? 'idle' : vWarn ? 'warn' : vp.live ? 'live' : 'idle';
  const ys = dests.map((_, i) => cy + (i - (n - 1) / 2) * step);
  const nodeH = compact ? 30 : 48;
  // the one heartbeat: the header logo wins when it animates (src/ui/motion.tsx)
  const loop = useBadgePulse();

  const upDips = dips(source.history).count;
  const uplink: { text: string; color: StateColor } = source.unknown ? { text: 'NO DATA', color: 'idle' }
    : !source.sending ? { text: 'NO STREAM', color: 'idle' }
    : !source.history.some((p) => p.v > 0) ? { text: 'NO DATA YET', color: 'idle' }
    : upDips ? { text: `${upDips} DIP${upDips > 1 ? 'S' : ''}`, color: 'warn' } : { text: '5 MIN OK', color: 'live' };
  const links: LinkInfo[] = [
    { id: 'uplink', label: 'Uplink from OBS', pill: uplink.text, color: uplink.color,
      history: source.history, event: source.summary.text, x: (((SRC_X + SRC_W + N_X) / 2) / VB_W) * 100, y: ((cy - 40) / H) * 100 },
    ...dests.map((d, i) => {
      const p = pillFor(d);
      return { id: String(d.id), label: `${d.name} link`, pill: p.text, color: p.color, history: d.history, event: d.summary.text, x: (D_X / VB_W) * 100, y: (ys[i]! / H) * 100, dest: d };
    }),
  ];
  const active = links.find((l) => l.id === open);
  const activeDelay = links.find((l) => l.dest?.delay && `delay:${l.id}` === open);
  const anyDelay = dests.some((d) => d.delay);
  const sent = useCounter(source.received, now);
  // with edit buttons on the nodes' right edge, the viewers count moves left of them
  const vx = onEdit ? D_X + D_W - 24 : D_X + D_W - 8;
  const srcState: StateColor = source.unknown ? 'idle' : source.sending ? 'live' : 'idle';
  const nodeState: StateColor = source.unknown ? 'warn' : source.sending ? 'live' : 'idle';
  const up = `M${SRC_X + SRC_W} ${cy} H${N_X}`;

  return (
    <section className="sx-map flex flex-col gap-3" aria-label="Signal map">
      <div className="flex items-center justify-between gap-3">
        <span className="sx-label">Signal map</span>
        {viewers && viewers.platforms > 0 && (
          <span className="flex items-center gap-1.5 text-[13px] text-muted">
            <EyeIcon />{viewersText(viewers.total)} watching on {viewers.platforms} platform{viewers.platforms === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${VB_W} ${H}`} width="100%" role="img" className="block font-mono"
          aria-label={`${source.app} on your PC ${source.sending ? 'sends' : 'is not sending'} the stream to us; we feed ${n} destinations.`}>

          {/* SOURCE */}
          <text x={SRC_X} y={cy - 64 - lift} className="fill-muted" fontSize="9" letterSpacing="1.5">SOURCE</text>
          <rect x={SRC_X} y={cy - 52 - lift} width={SRC_W} height={122 + lift + down + extra} rx="3" className="sx-node on" style={{ '--c': `var(--${srcState})` } as CSSProperties} />
          <g transform={`translate(0 ${-lift})`}>
            <MapIcon name="desktop" x={26} y={cy - 38} size={16} className="text-muted" />
            <text x="50" y={cy - 26} className="fill-muted" fontSize="9" letterSpacing="1.5">
              YOUR PC
              {source.sending && source.protocol && (
                <tspan data-testid="map-protocol" className="fill-text-soft">
                  {' · '}{source.protocol.toUpperCase()}
                  <title>How your encoder sends the stream</title>
                </tspan>
              )}
            </text>
          </g>
          <g transform={`translate(0 ${down})`}>
            <text x="26" y={cy + 5} className="fill-text" fontSize="14" fontWeight="500">{source.app}</text>
            <MapIcon name={`state-${srcState}`} x={26} y={cy + 14} className={TXT[srcState]} />
            <text x="42" y={cy + 23} className={FILL[srcState]} fontSize="10" letterSpacing=".5">{source.sending ? 'SENDING' : 'NOT SENDING'}</text>
            <text x="26" y={cy + 41} className="fill-text num" fontSize="10" letterSpacing=".5">↑ {sent} · {source.uploadMbps !== undefined ? `${source.uploadMbps.toFixed(1)} Mbps` : '—'}</text>
            {dl && (
              <g data-testid="input-delay">
                <title>Estimated from network round-trip (RTT) and how far OBS is behind its own clock</title>
                <text x="26" y={cy + 60} fontSize="10" letterSpacing=".5">
                  <tspan className="fill-muted" fontWeight="600">DELAY TO US </tspan>
                  <tspan className="fill-text num" fontSize="12">≈{delaySec(dl.toUsMs)}</tspan>
                </text>
                <text x="26" y={cy + 76} className="fill-muted num" fontSize="9.5" letterSpacing=".3">
                  RTT {dl.rttMs > 0 ? `${Math.round(dl.rttMs)} ms` : '—'} · backlog {backlogSec(dl.backlogMs)}
                </text>
              </g>
            )}
            <text x="26" y={cy + 58 + extra} className="fill-muted" fontSize="10" letterSpacing=".5">JITTER {source.jitterMs !== undefined ? `${source.jitterMs} ms` : '—'}</text>
          </g>

          {/* UPLINK: the view's one travelling dash while OBS sends */}
          <path d={up} fill="none" className="stroke-live" strokeOpacity={open === 'uplink' ? 0.25 : 0} strokeWidth="12" strokeLinecap="round" />
          <path d={up} className={`sx-route ${source.sending ? 'live' : 'idle'}`} style={{ '--c': 'var(--live)' } as CSSProperties} />
          {source.sending && loop && <path d={up} data-testid="map-flow" className="sx-route flow" style={{ '--c': 'var(--text)', opacity: 0.8 } as CSSProperties} />}

          {/* destination routes */}
          {dests.map((d, i) => {
            const col: StateColor = d.issue ? 'warn' : nodeLine(d, now).color;
            const y = ys[i]!;
            const key = String(d.id);
            const failed = d.state === 'failed';
            const from = vp && fromVertical(d) ? cy + V_PORT : cy;
            const path = routeFor(from, y, failed ? D_X - 22 : D_X);
            const kind = failed || d.state === 'live' && !d.issue ? '' : col === 'warn' || d.state === 'starting' ? 'warn' : 'idle';
            return (
              <g key={d.id} data-route={kind || 'solid'}>
                <path d={path} fill="none" className={STROKE[col]} strokeOpacity={open === key ? 0.25 : 0} strokeWidth="12" strokeLinecap="round" />
                {d.state === 'stopping' ? (
                  <path d={path} pathLength={200} fill="none" className="animate-retract stroke-idle" strokeWidth="2" strokeDasharray="200" />
                ) : (
                  <path d={path} className={`sx-route ${kind}`} style={{ '--c': `var(--${col})` } as CSSProperties} />
                )}
                {failed && <path d={`M${D_X - 18} ${y - 6}l8 12M${D_X - 10} ${y - 6}l-8 12`} className="stroke-fail" strokeWidth="2" />}
              </g>
            );
          })}

          {/* VERT-1: the vertical source under the main one, its line into the node's lower port (VT-03); warn over 1 s of drift (VT-16) */}
          {vp && (
            <g data-testid="map-vertical" data-state={vState}>
              <rect x={SRC_X} y={vTop} width={SRC_W} height={V_H} rx="3" className="sx-node on" style={{ '--c': `var(--${vState})` } as CSSProperties} />
              <text x="26" y={vTop + 22} className="fill-text" fontSize="13" fontWeight="500">{vp.app} · {t('vert.row.vertical').toLowerCase()}</text>
              <text x="26" y={vTop + 42} className="fill-muted num" fontSize="10" letterSpacing=".5">
                {vp.live ? `${verticalVideo(vp.resolution, vp.fps) ?? '—'} · ${vp.bitrateMbps !== undefined ? `${vp.bitrateMbps.toFixed(1)} Mbps` : '—'}${vp.protocol ? ` · ${vp.protocol.toUpperCase()}` : ''}` : t('vert.f.listening').toUpperCase()}
              </text>
              {vp.live && (
                <g data-testid="map-vertical-drift">
                  {vWarn && <MapIcon name="state-warn" x={26} y={vTop + 51} className="text-warn" />}
                  <text x={vWarn ? 42 : 26} y={vTop + 61} className={vWarn ? 'fill-warn num' : 'fill-muted num'} fontSize="10" letterSpacing=".5">
                    {t('vert.sum.drift').toUpperCase()} {driftText(vp.driftMs)}
                  </text>
                </g>
              )}
              {/* 0° out, 90° up, 45° into the lower port (05 «45° geometry») */}
              <path d={`M${SRC_X + SRC_W} ${vy} H${N_X - 40} V${cy + V_PORT + 40} L${N_X} ${cy + V_PORT}`} className={`sx-route ${vp.live ? (vWarn ? 'warn' : 'live') : 'idle'}`} style={{ '--c': `var(--${vState === 'idle' ? 'live' : vState})` } as CSSProperties} />
            </g>
          )}

          {/* the ingest node */}
          <rect x={N_X} y={cy - 26} width={N_W} height={vp ? 52 + V_PORT : 52} rx="3" className="sx-node on" style={{ '--c': `var(--${nodeState})` } as CSSProperties} />
          <text data-testid="map-node" x={N_X + 12} y={cy - 6} className={source.unknown ? 'fill-warn' : 'fill-text'} fontSize="11" fontWeight="500" letterSpacing=".5">
            <title>{source.unknown ? 'Media server unreachable, state unknown' : 'Media server your stream came in on'}</title>
            {source.node ? `${serverName(source.node).toUpperCase()}${source.unknown ? ' ?' : ''}` : 'INGEST'}
          </text>
          <text x={N_X + 12} y={cy + 12} className="fill-muted num" fontSize="10" letterSpacing=".5">{source.uploadMbps !== undefined ? `${source.uploadMbps.toFixed(1)} Mbps in` : '—'}</text>
          {vp && (
            // VT-03: the lower port of the node carries the vertical input; its tag is 9:16
            <g data-testid="map-vertical-port">
              <rect x={N_X + N_W + 6} y={cy + V_PORT - 8} width="30" height="16" rx="2" className="fill-bg stroke-line-2" strokeWidth="1" />
              <text x={N_X + N_W + 21} y={cy + V_PORT + 4} textAnchor="middle" className="fill-text-soft" fontSize="9" letterSpacing=".4">{t('vert.tag')}</text>
            </g>
          )}

          {/* destination nodes */}
          {dests.map((d, i) => {
            const nl = nodeLine(d, now);
            const y = ys[i]!;
            const nx = compact ? D_X + 12 : D_X + 52;
            return (
              <g key={d.id}>
                <rect x={D_X} y={y - nodeH / 2} width={D_W} height={nodeH} rx="3" className="sx-node on" style={{ '--c': `var(--${nl.color})` } as CSSProperties} />
                {!compact && (
                  <foreignObject x={D_X + 10} y={y - 15} width="30" height="30">
                    <LogoSlot platform={d.platform} dim={!d.enabled} />
                  </foreignObject>
                )}
                <text x={nx} y={compact ? y + 4 : y - 4} className="fill-text font-sans" fontSize="13" fontWeight="500">{shortName(d.name, (d.viewers !== undefined ? (onEdit ? 11 : 13) : onEdit ? 15 : 18) - (vp && fromVertical(d) && !compact ? 4 : 0))}</text>
                {d.viewers !== undefined && (
                  <g data-testid={`map-viewers-${d.id}`}>
                    <title>{`Watching now on ${d.profileName}, via its API`}</title>
                    <text x={vx} y={compact ? y + 4 : y - 4} textAnchor="end" className="fill-text num" fontSize="11">{viewersText(d.viewers)}</text>
                    <MapIcon name="eye" x={vx - viewersText(d.viewers).length * 6.7 - 16} y={(compact ? y + 4 : y - 4) - 10} className="text-muted" />
                  </g>
                )}
                {vp && fromVertical(d) && !compact && (
                  <g data-testid={`map-tag-${d.id}`}>
                    <rect x={D_X + D_W - (onEdit ? 64 : 42)} y={y - 17} width="30" height="15" rx="2" className="fill-bg stroke-line-2" strokeWidth="1" />
                    <text x={D_X + D_W - (onEdit ? 49 : 27)} y={y - 6} textAnchor="middle" className="fill-text-soft" fontSize="9" letterSpacing=".4">{t('vert.tag')}</text>
                  </g>
                )}
                {!compact && (
                  <>
                    <MapIcon name={`state-${nl.color}`} x={nx} y={y + 4} className={TXT[nl.color]} turn={nl.color === 'start'} />
                    <text x={nx + 16} y={y + 13} className={FILL[nl.color]} fontSize="10" letterSpacing=".4">{nl.text}</text>
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {/* OBS edit button on the source node */}
        <div className="absolute" style={{ left: `${((SRC_X + SRC_W - 4) / VB_W) * 100}%`, top: `${((cy - 48 - lift) / H) * 100}%`, transform: 'translateX(-100%)' }}>
          <EditButton editing={obsOpen} label="OBS settings" onClick={onObs} />
        </div>
        {/* edit buttons on the destination nodes' right edge: the settings open under the row of the table */}
        {onEdit && dests.map((d, i) => (
          <div key={d.id} data-testid={`map-edit-${d.id}`} className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${((D_X + D_W) / VB_W) * 100}%`, top: `${(ys[i]! / H) * 100}%` }}>
            <EditButton editing={editing === d.id} attention={d.needsKeyFix || d.needsKey} label={d.name} onClick={() => onEdit(d.id)} />
          </div>
        ))}
        {/* Stream preview inside the source node; scales with the map */}
        {pv && (
          <div className="absolute" style={{ left: `${(PV_X / VB_W) * 100}%`, top: `${(pvY(cy) / H) * 100}%`, width: `${(PV_W / VB_W) * 100}%` }}>
            <StreamPreview stream={pv.stream} available={pv.available} now={now} width={242} height={136} fluid />
          </div>
        )}
        {obsOpen && <ObsPopover top={`${((cy + 76 + down + extra) / H) * 100}%`} streamName={source.streamName} protocol={source.protocol} onClose={onObsClose} />}

        {/* 2B: summary labels on routes; with a delay the label gets a second half (docs/m15-live-stats.md 1.3) */}
        {links.map((l) => {
          const panel = (id: string) => ({
            'data-panel-for': id,
            'aria-expanded': open === id,
            onMouseEnter: () => setOpen(id),
            onMouseLeave: () => setOpen(pinned),
            onFocus: () => !restoring.current && setOpen(id),
            onBlur: () => setOpen(pinned),
            onClick: () => (pinned === id ? (setPinned(null), setOpen(null)) : (setPinned(id), setOpen(id))),
          });
          const d = l.dest;
          const sw = d && onToggle;
          // a destination's label stands on the straight run just before its node, its left edge at a
          // fixed distance from the node: room for the toggle, the longest label and (if any line has
          // one) the delay half
          const left = d ? `calc(${l.x}% - ${12 + (sw ? 40 : 0) + 112 + (anyDelay ? 84 : 0)}px)` : `${l.x}%`;
          // A destination's chip keeps its place while the state changes (owner, 19.10: the toggle
          // jumped): the label has the width of the longest one ("NO DATA YET"), and with a toggle
          // the chip hangs from a fixed point left of the label's middle with the toggle first, so
          // whatever the label or the delay says, the toggle stays under the pointer. The edge is
          // shape-coded like a chip: warn dashed, idle dotted (02 «state is never colour alone»).
          return (
            <span key={l.id} data-testid={d ? `line-chip-${d.id}` : undefined} data-state={l.color}
              className={`absolute flex min-h-[26px] -translate-y-1/2 items-stretch whitespace-nowrap rounded-md border bg-panel ${d ? '' : '-translate-x-1/2'} ${BORDER[l.color]} ${EDGE[l.color]}`}
              style={{ left, top: `${l.y}%` }}>
              {sw && (
                <span className={`flex items-center rounded-l-md border-r px-0.5 ${BORDER50[l.color]} ${EDGE[l.color]}`}>
                  <RelayToggle on={d.enabled} busy={d.busy} size="sm" testId={`line-toggle-${d.id}`}
                    label={`${d.name}: ${d.enabled ? 'turn off' : 'turn on'}`} onToggle={() => onToggle(d)} />
                </span>
              )}
              <button type="button" {...panel(l.id)}
                aria-label={`${l.label}: ${l.pill.toLowerCase()}. History of the last 5 minutes`}
                className={`flex items-center gap-1 px-2 text-left font-mono text-[10px] font-medium tracking-[.06em] ${d ? 'min-w-[calc(11ch+11*.06em+32px)]' : ''} ${TXT[l.color]} ${open === l.id ? BG12[l.color] : ''} ${sw ? '' : 'rounded-l-md'} ${d?.delay ? '' : 'rounded-r-md'}`}>
                <Icon name={`state-${l.color}`} size={12} />
                {l.pill}
              </button>
              {d?.delay && <DelayHalf name={d.name} delay={d.delay} color={l.color} open={open === `delay:${l.id}`} props={panel(`delay:${l.id}`)} id={d.id} />}
            </span>
          );
        })}
        {active && <HistoryPopover link={active} pinned={pinned === active.id} />}
        {activeDelay?.dest?.delay && <DelayBreakdown link={activeDelay} dest={activeDelay.dest} delay={activeDelay.dest.delay} pinned={pinned === open} />}
      </div>
      {source.srt && <SrtLinkPanel link={source.srt} />}
      <p className="m-0 text-[13px] text-muted">
        {anyDelay
          ? 'Each route is one destination. Hover the left of a label for its last 5 minutes, the clock for its delay; click to pin.'
          : 'Each route is one destination. Hover a label for its last 5 minutes; click to pin.'}
      </p>
    </section>
  );
}

/** chip edges by state, as the system's .sx-chip: warn dashed, idle dotted */
const EDGE: Record<StateColor, string> = { live: '', start: '', fail: '', warn: 'border-dashed', idle: 'border-dotted' };

function HistoryPopover({ link, pinned }: { link: LinkInfo; pinned: boolean }) {
  const W = 248, H = 52;
  const p = wavePaths(link.history, W, H);
  const below = link.y <= 52;
  const last = link.history[link.history.length - 1];
  return (
    <div role="dialog" aria-label={`${link.label} history`}
      className="absolute z-[5] flex w-[284px] flex-col gap-2 rounded-lg border border-line-2 bg-panel p-3 shadow-pop"
      style={{ left: `${link.x}%`, top: `${link.y}%`, transform: `translate(${link.x < 30 ? '-15%' : '-50%'}, ${below ? '18px' : 'calc(-100% - 18px)'})` }}>
      <div className="flex items-baseline justify-between gap-2"><span className="text-[13px] font-semibold">{link.label}</span><span className="font-mono text-[10px] tracking-[.08em] text-muted">LAST 5 MIN</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} aria-hidden="true" className="block rounded-md bg-bg">
        <path d={p.target} className="stroke-line" strokeDasharray="2 4" fill="none" vectorEffect="non-scaling-stroke" />
        <path d={p.idle} className="stroke-slot" strokeDasharray="2 3" fill="none" vectorEffect="non-scaling-stroke" />
        <path d={p.good} className="stroke-live" strokeWidth="1.6" fill="none" vectorEffect="non-scaling-stroke" />
        <path d={p.warn} className="stroke-warn" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
        <path d={p.bad} className="stroke-fail" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-muted"><span>5 min ago</span><span>now</span></div>
      <div className="font-mono text-[11px] text-text-soft">now {!last || last.v < 0 ? '—' : last.v.toFixed(1)} · {historyStat(link.history)} Mbps</div>
      <div className={`text-xs ${TXT[link.color]}`}>{link.event}</div>
      <div className="text-[11px] text-muted">{pinned ? 'Pinned. Click the label again to close.' : 'Click the label to pin this panel.'}</div>
    </div>
  );
}

const shortName = (name: string, max: number) => (name.length > max ? name.slice(0, max - 1) + '…' : name);

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true" className="flex-none">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/** Chip text of a delay: full rounded, ours with ≈ (OBS → us is an estimate), none a dash. */
export function delayText(dl: LineDelay): string {
  if (dl.kind === 'none' || dl.totalMs === undefined) return '—';
  return dl.kind === 'ours' ? `≈${delaySec(dl.totalMs)}` : delaySec(dl.totalMs);
}

function delayTitle(name: string, dl: LineDelay): string {
  if (dl.kind === 'none' || dl.totalMs === undefined) return 'No delay while not connected';
  return dl.kind === 'full' ? `Delay from OBS to viewers on ${name}` : `Delay from OBS to our server. ${name} does not report the rest`;
}

/** The clock half of a line chip: the delay; hover or focus opens the breakdown, click pins it. */
function DelayHalf({ id, name, delay, color, open, props }: {
  id: number; name: string; delay: LineDelay; color: StateColor; open: boolean; props: Record<string, unknown>;
}) {
  const none = delay.kind === 'none' || delay.totalMs === undefined;
  const tone = delay.warning ? 'text-warn' : none ? 'text-faint' : 'text-text';
  return (
    <button type="button" {...props} data-testid={`line-delay-${id}`} data-kind={delay.kind} data-warning={delay.warning ? 'true' : 'false'}
      aria-label={`${delayTitle(name, delay)}: ${delayText(delay)}${delay.kind === 'ours' ? ' to server' : ''}. Breakdown`}
      title={delayTitle(name, delay)}
      className={`flex items-center gap-1 rounded-r-md border-l px-2 font-mono text-[11px] font-medium num ${BORDER50[color]} ${tone} ${open ? 'bg-accent/10' : ''}`}>
      <ClockIcon />
      <span>{delayText(delay)}</span>
      {delay.kind === 'ours' && !none && <>{' '}<span className="text-[10px] font-normal text-muted">to server</span></>}
    </button>
  );
}

const LATENCY: Record<string, string> = { normal: 'Normal latency', low: 'Low latency mode', ultraLow: 'Ultra-low latency' };

/** Where the delay comes from, line by line (docs/m15-live-stats-ui-handoff.md 2). */
function DelayBreakdown({ link, dest, delay, pinned }: { link: LinkInfo; dest: Destination; delay: LineDelay; pinned: boolean }) {
  const below = link.y <= 52;
  const where = dest.platform === 'rtmp' ? dest.name : dest.profileName;
  const none = delay.kind === 'none' || delay.totalMs === undefined;
  const toUs = delay.toUsMs ?? (delay.kind === 'ours' ? delay.totalMs : undefined);
  const rows: { k: string; v: string; src: string }[] = none ? [] : [
    { k: 'OBS → us', v: toUs !== undefined ? `≈${delaySec(toUs)}` : '—', src: delay.rttMs ? `≈ RTT ${Math.round(delay.rttMs)} ms` : '≈ estimated' },
    ...(delay.kind === 'full' ? [{ k: `us → ${where}`, v: delaySec(delay.toPlatformMs), src: 'measured' }] : []),
    delay.kind === 'full' && delay.platformToViewersMs !== undefined
      ? { k: `${where} → viewers`, v: `≈${delaySec(delay.platformToViewersMs)}`, src: delay.latencyMode ? LATENCY[delay.latencyMode] ?? delay.latencyMode : 'typical' }
      : { k: `${where} → viewers`, v: '—', src: 'not shared' },
  ];
  return (
    <div role="dialog" aria-label={`${dest.name} delay`} data-testid="delay-breakdown" data-kind={delay.kind}
      className="absolute z-[5] flex w-[260px] flex-col rounded-lg border border-accent bg-panel px-3 py-2.5 shadow-pop"
      style={{ left: `${link.x}%`, top: `${link.y}%`, transform: `translate(-30%, ${below ? '18px' : 'calc(-100% - 18px)'})` }}>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="truncate font-mono text-[10px] font-semibold tracking-[.12em] text-muted">DELAY · {dest.name.toUpperCase()}</span>
        <span className={`font-mono text-sm font-semibold num ${delay.warning ? 'text-warn' : ''}`}>{delayText(delay)}</span>
      </div>
      {none ? (
        <p className="m-0 border-t border-line py-1.5 text-xs text-text-soft">No delay while not connected.</p>
      ) : (
        rows.map((r) => (
          <div key={r.k} className="grid grid-cols-[1fr_auto] gap-x-3.5 gap-y-0.5 border-t border-line py-1.5">
            <span className="text-xs text-text-soft">{r.k}</span>
            <span className="text-right font-mono text-xs font-medium num">{r.v}</span>
            <span className="col-span-2 text-[10px] text-faint">{r.src}</span>
          </div>
        ))
      )}
      {!none && delay.aboveMedian && (
        <div data-testid="delay-above-median" className="border-t border-line py-1.5 text-[11px] text-text-soft">Higher than usual for this line. The stream is fine unless the card shows a problem.</div>
      )}
      {!none && delay.link && <LinkDiagnostics where={where} link={delay.link} />}
      {!none && <div className="border-t border-line pt-1.5 text-[10px] text-faint">≈ estimated: RTMP carries no wall clock.</div>}
      <div className="pt-1 text-[11px] text-muted">{pinned ? 'Pinned. Click the clock again to close.' : 'Click the clock to pin this panel.'}</div>
    </div>
  );
}

const ms = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`);

/**
 * Network numbers of the us → platform link, always neutral: late acks (YouTube ~10 s), retransmissions
 * and RTT swings are normal on long routes; a link that hurts the stream shows as SLOW LINK on the card.
 */
function LinkDiagnostics({ where, link }: { where: string; link: LinkDiag }) {
  const parts = [
    link.rttMs !== undefined ? `RTT ${Math.round(link.rttMs)} ms` : '',
    link.retransPct !== undefined ? `resent ${link.retransPct.toFixed(1)}%` : '',
    link.cc ? link.cc.toUpperCase() : '',
  ].filter(Boolean);
  const more = [
    link.ackDelayMs !== undefined ? `acks ${ms(link.ackDelayMs)}` : '',
    link.sendQueueMs !== undefined ? `queue ${ms(link.sendQueueMs)}` : '',
  ].filter(Boolean);
  return (
    <div data-testid="link-diagnostics" className="border-t border-line py-1.5">
      <div className="text-xs text-text-soft">Network to {where}</div>
      {parts.length > 0 && <div className="font-mono text-[11px] text-muted num">{parts.join(' · ')}</div>}
      {more.length > 0 && <div className="font-mono text-[11px] text-muted num">{more.join(' · ')}</div>}
      <div className="text-[10px] text-faint">Normal swings on long routes. A problem shows on the card.</div>
    </div>
  );
}

/** OBS settings under the source node. Closes with Done, Escape or a click outside it. */
function ObsPopover({ top, streamName, protocol, onClose }: { top: string; streamName?: string; protocol?: string; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (ref.current?.contains(t) || t?.closest('[aria-label="Edit OBS settings"], [aria-label="Close settings for OBS settings"]')) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key); };
  }, [onClose]);
  return (
    <div ref={ref} role="dialog" aria-label="OBS settings" className="absolute z-10 w-[360px] animate-condense rounded-lg border border-accent bg-panel p-3.5 shadow-pop"
      style={{ left: '1.2%', top }}>
      <ObsSettings streamName={streamName} protocol={protocol} onDone={onClose} />
    </div>
  );
}
